/**
 * The `<head>` of one language's landing page.
 *
 * Shared by `routes/index.tsx` (English, at `/`) and `routes/$lang.tsx` (the
 * other eight). Two routes render the same page and therefore have to make the
 * same claims about it — a canonical or an hreflang set that differs between
 * them is precisely the bug that makes Google drop the whole language cluster,
 * and it is invisible in a browser.
 */

import {
  alternates,
  appJsonLd,
  canonical,
  faqJsonLd,
  organizationJsonLd,
  socialMeta,
  websiteJsonLd,
} from "../seo";
import type { ContentLang } from "./registry";
import { langPath } from "./registry";
import type { MarketingBundle } from "./types";

export function landingHead(lang: ContentLang, bundle: MarketingBundle) {
  const path = langPath(lang);
  return {
    meta: [
      { title: bundle.title },
      { name: "description", content: bundle.description },
      ...socialMeta({
        title: bundle.title,
        description: bundle.description,
        path,
        lang,
        imageAlt: bundle.ogTagline,
      }),
      // Emitted as `<script type="application/ld+json">` by TanStack's head
      // renderer, which also escapes it — see `headContentUtils`. Three separate
      // blocks rather than one `@graph`: Google's Rich Results Test reports per
      // block, so a mistake in the FAQ does not take the app entity down with it.
      { "script:ld+json": appJsonLd(bundle.title, bundle.description) },
      { "script:ld+json": faqJsonLd(bundle.faq.items) },
      { "script:ld+json": organizationJsonLd() },
      // The site as an entity, tying the app, the blog and the legal pages to
      // one publisher — see `websiteJsonLd` for why it declares no SearchAction.
      { "script:ld+json": websiteJsonLd(bundle.title, bundle.description) },
    ],
    links: [
      { rel: "canonical", href: canonical(path) },
      ...alternates(langPath),
      // The hero screenshot is the LCP element on a phone. Preloading it starts
      // the request during head parsing rather than after the layout reaches the
      // <img>, which on a cold 4G connection is most of a second.
      {
        rel: "preload",
        as: "image",
        href: `/shots/${lang}/${bundle.shots.items[0].file}.webp`,
        // `imageSrcSet`/`imageSizes` must MIRROR the <img> exactly, or the
        // preload fetches the 640 while the image element then picks the 320
        // and the page downloads both. A mismatched preload is worse than none.
        imageSrcSet: `/shots/${lang}/${bundle.shots.items[0].file}-320.webp 320w, /shots/${lang}/${bundle.shots.items[0].file}.webp 640w`,
        imageSizes:
          "(min-width: 1024px) 300px, (min-width: 640px) 280px, 240px",
        type: "image/webp",
        fetchPriority: "high",
      },
    ],
  };
}
